import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Btn, InputField } from './UIComponents';
import api from '../../services/api';

export default function MedicationModal({ onClose, medication, isEdit }) {
  const { B } = useTheme();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    resident_id: medication?.resident_id || '',
    name: medication?.name || '',
    dosage: medication?.dosage || '',
    route: medication?.route || '',
    frequency: medication?.frequency || '',
    scheduled_time: medication?.scheduled_time || '',
    prescriber: medication?.prescriber || '',
    start_date: medication?.start_date || '',
    end_date: medication?.end_date || '',
    notes: medication?.notes || ''
  });

  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const res = await api.get('/residents', { params: { limit: 100, status: 'active' } });
        setResidents(res.data.data);
      } catch (err) {
        console.error('Failed to fetch residents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResidents();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.resident_id) {
      alert('Please select a resident');
      return;
    }
    if (!formData.name) {
      alert('Please enter medication name');
      return;
    }
    if (!formData.scheduled_time) {
      alert('Please select scheduled time');
      return;
    }

    try {
      if (isEdit && medication?.id) {
        await api.put(`/emar/medications/${medication.id}`, formData);
      } else {
        await api.post('/emar/medications', formData);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save medication:', err);
      alert(err.response?.data?.message || 'Failed to save medication');
    }
  };

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    '@media (maxWidth: 768px)': {
      gridTemplateColumns: '1fr',
    },
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1.5px solid ${B.border}`,
    fontSize: 13,
    background: B.inputBg,
    color: B.text,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 16,
    '@media (maxWidth: 768px)': {
      flexDirection: 'column',
      gap: 8,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.navy, marginBottom: 20 }}>
          {isEdit ? 'Edit Medication' : 'Add Medication'}
        </div>

        {/* Resident Selection */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Resident <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            name="resident_id"
            value={formData.resident_id}
            onChange={handleChange}
            style={inputStyle}
            disabled={loading}
          >
            <option value="">
              {loading ? 'Loading residents...' : 'Select a resident...'}
            </option>
            {residents.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} (Room {r.room_number})
              </option>
            ))}
          </select>
        </div>

        <div style={gridStyle}>
          <InputField
            label="Medication Name *"
            name="name"
            placeholder="e.g. Lisinopril"
            value={formData.name}
            onChange={handleChange}
          />
          <InputField
            label="Dosage"
            name="dosage"
            placeholder="e.g. 10mg"
            value={formData.dosage}
            onChange={handleChange}
          />
        </div>

        <div style={gridStyle}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
              Route
            </label>
            <select
              name="route"
              value={formData.route}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select...</option>
              <option value="Oral">Oral</option>
              <option value="SubQ">SubQ</option>
              <option value="Transdermal">Transdermal</option>
              <option value="Inhalation">Inhalation</option>
              <option value="IV">IV</option>
              <option value="IM">IM</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
              Frequency
            </label>
            <select
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select...</option>
              <option value="Daily">Daily</option>
              <option value="BID">BID (Twice daily)</option>
              <option value="TID">TID (Three times daily)</option>
              <option value="QID">QID (Four times daily)</option>
              <option value="QHS">QHS (At bedtime)</option>
              <option value="Q4H PRN">Q4H PRN</option>
              <option value="Q72H">Q72H</option>
              <option value="PRN">PRN (As needed)</option>
            </select>
          </div>
        </div>

        <div style={gridStyle}>
          <InputField
            label="Start Date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleChange}
          />
          <InputField
            label="Scheduled Time *"
            name="scheduled_time"
            type="time"
            value={formData.scheduled_time}
            onChange={handleChange}
          />
        </div>

        <InputField
          label="Prescriber"
          name="prescriber"
          placeholder="e.g. Dr. Smith"
          value={formData.prescriber}
          onChange={handleChange}
        />

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Special instructions..."
            rows="3"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={buttonContainerStyle}>
          <Btn outline color={B.muted} onClick={onClose}>
            Cancel
          </Btn>
          <Btn color={B.green} onClick={handleSubmit}>
            {isEdit ? 'Update Medication' : 'Add Medication'}
          </Btn>
        </div>
      </div>
    </div>
  );
}